<template>
    <ModalDialog @close="handleClose" :show="props.show">
        <template #header-title>Download report</template>
        <div class="flex flex-col gap-2 xl:gap-4 transition-all duration-300">
            <!-- File type -->
            <div class="flex flex-col gap-2">
                <div class="text-sm font-medium">File type</div>
                <div class="bg-system-light rounded-lg py-4 xl:py-6 px-4 transition-all duration-300">
                    <RadioGroup
                        v-model="interfaceStore.preferences.downloadReport!.fileType"
                        :options="[
                            { value: 'pdf', label: 'PDF' },
                            { value: 'xls', label: 'XLS' }
                        ]"
                        orientation="horizontal"
                    />
                </div>
            </div>

            <!-- Timeline -->
            <!-- <div class="flex flex-col gap-2">
                <div class="text-sm font-medium flex flex-row gap-2 items-end text-balance">
                    <div class="flex flex-row gap-1 items-center">
                        <span>Timeline</span>
                        <TappTooltip label="Report will be recalculated based on timeline" position="up" class="flex items-center justify-center cursor-pointer pt-0.5">
                            <div
                                class="[font-size:0.625rem] border border-gray-300 cursor-pointer text-default tabular-nums aspect-square rounded-full inline-flex items-center justify-center w-4 h-4 font-semibold"
                            >
                                ?
                            </div>
                        </TappTooltip>
                    </div>
                    <span class="text-xs text-sub tabular-nums">{{ formattedTimelineRange }}</span>
                </div>
                <div class="bg-system-light rounded-lg py-4 xl:py-6 px-4 transition-all duration-300">
                    <TappRadioGroup
                        v-model="timelinePreference"
                        :options="[
                            { value: 'full', label: 'Full timeline' },
                            { value: 'trimmed', label: 'Selected timeline', disabled: !timelineIsTrimmed }
                        ]"
                        orientation="horizontal"
                    />
                </div>
            </div> -->

            <!-- Data table -->
            <div class="flex flex-col gap-2" v-if="interfaceStore.preferences.downloadReport?.fileType === 'pdf'">
                <div class="text-sm font-medium flex flex-row gap-1 items-center">
                    <span>Report style</span>
                    <Tooltip label="The data table lists all measurements" position="up" class="flex items-center justify-center cursor-pointer pt-0.5">
                        <div
                            class="[font-size:0.625rem] border border-gray-300 cursor-pointer text-default tabular-nums aspect-square rounded-full inline-flex items-center justify-center w-4 h-4 font-semibold"
                        >
                            ?
                        </div>
                    </Tooltip>
                </div>
                <div class="bg-system-light rounded-lg py-4 xl:py-6 px-4 transition-all duration-300">
                    <RadioGroup
                        v-model="interfaceStore.preferences.downloadReport.includeDataTable"
                        :options="[
                            { value: 'minimal', label: 'Minimal' },
                            { value: 'full', label: 'Include data table' }
                        ]"
                        orientation="horizontal"
                    />
                </div>
            </div>

            <!-- Temperature unit -->
            <div class="flex flex-col gap-2" v-if="props.labelReport.type === SenseType.TempSense || props.labelReport.type === SenseType.HumiditySense">
                <div class="text-sm font-medium">Temperature unit</div>
                <div class="bg-system-light rounded-lg py-4 xl:py-6 px-4 transition-all duration-300">
                    <RadioGroup
                        v-model="interfaceStore.preferences.downloadReport!.temperatureUnit"
                        :options="[
                            { value: 'celsius', label: 'Celsius' },
                            { value: 'fahrenheit', label: 'Fahrenheit' }
                        ]"
                        orientation="horizontal"
                    />
                </div>
            </div>

            <!-- Timezone -->
            <div class="flex flex-col gap-2">
                <div class="text-sm font-medium">Time zone</div>
                <TimeZoneSelect v-model="interfaceStore.preferences.downloadReport!.timeZone" size="lg" class="w-full max-w-full" rounded="md" />
            </div>

            <!-- Locale -->
            <!-- <div class="flex flex-col gap-2">
                <div class="text-sm font-medium">Language</div>
                <TappSelect
                    v-model="interfaceStore.preferences.downloadReport.locale"
                    :options="[
                        { value: 'en-US', label: 'English' }
                        // { value: 'es-ES', label: 'Español' },
                        // { value: 'nl-NL', label: 'Nederlands' }
                    ]"
                    size="lg"
                    class="w-full max-w-full"
                    rounded="md"
                />
            </div> -->

            <!-- Page size -->
            <!-- <div class="flex flex-col gap-2" v-if="interfaceStore?.preferences.downloadReport?.fileType === 'pdf'">
                <div class="text-sm font-medium">Page size</div>
                <div class="bg-system-light rounded-lg py-4 xl:py-6 px-4 transition-all duration-300">
                    <TappRadioGroup
                        v-model="interfaceStore?.preferences.downloadReport.pageSize"
                        :options="[
                            { value: 'a4', label: 'A4' },
                            { value: 'us-letter', label: 'US Letter' }
                        ]"
                        orientation="horizontal"
                    />
                </div>
            </div> -->

            <div class="pt-4 w-full">
                <Button label="Download" loadingLabel="Downloading" size="lg" class="w-full" @click="handleDownloadLabelReport" :loading="isGeneratingLabelReport" />
            </div>
        </div>
    </ModalDialog>
</template>

<script setup lang="ts">
    import { ref, computed } from 'vue'
    import { usePostData } from '@/composables'
    import { useInterfaceStore } from '@/stores/interface'
    // import { SenseType } from '@/types/labelReports'
    // import { timestampToDateTime } from '@/utils/date'
    import { Button, Tooltip, useSnackbar, SnackbarType, RadioGroup, TimeZoneSelect } from '@tapp.online/ui'
    import ModalDialog from '@/components/elements/ModalDialog.vue'
    import { SenseType } from '@/types/enums'

    const interfaceStore = useInterfaceStore()
    const { addSnackbar } = useSnackbar()

    const emit = defineEmits(['close'])
    const props = defineProps<{
        show: boolean
        id?: string
        labelReport: any
        fullStartTime?: number
        fullEndTime?: number
        selectedStartTime?: number
        selectedEndTime?: number
    }>()
    const timelinePreference = ref('full')
    const timelineIsTrimmed = computed(() => {
        return props.selectedStartTime !== props.fullStartTime || props.selectedEndTime !== props.fullEndTime
    })

    const startTime = computed(() => (timelinePreference.value === 'full' ? props.fullStartTime : props.selectedStartTime))
    const endTime = computed(() => (timelinePreference.value === 'full' ? props.fullEndTime : props.selectedEndTime))

    // const formattedTimelineRange = computed(() => {
    //     if (!startTime.value || !endTime.value) return ''

    //     const formattedStartTime = timestampToDateTime(startTime.value, {
    //         timeZone: interfaceStore.preferences.downloadReport?.timeZone,
    //         locale: interfaceStore.preferences.downloadReport?.locale
    //     })
    //     const formattedEndTime = timestampToDateTime(endTime.value, {
    //         timeZone: interfaceStore.preferences.downloadReport?.timeZone,
    //         locale: interfaceStore.preferences.downloadReport?.locale
    //     })

    //     return `${formattedStartTime} – ${formattedEndTime}`
    // })

    const readAccessToken = computed(() => props.labelReport.readAccessToken)
    const handleClose = () => {
        emit('close')
    }

    const FileExtensions = {
        pdf: 'pdf',
        xls: 'xlsx'
    } as const

    // Handle pdf download
    const isGeneratingLabelReport = ref(false)
    const downloadLabelReport = usePostData()
    const handleDownloadLabelReport = async () => {
        const payload: Record<string, any> = {
            id: props.id,
            responseType: 'presignedUrl',
            reportType: interfaceStore.preferences.downloadReport?.fileType ?? 'pdf',
            timeZone: interfaceStore.preferences.downloadReport?.timeZone ?? 'utc',
            // locale: interfaceStore.preferences.downloadReport?.locale ?? 'en-US',
            temperatureUnit: interfaceStore.preferences.downloadReport?.temperatureUnit ?? 'celsius',
            includeDataTable: interfaceStore.preferences.downloadReport?.includeDataTable === 'full' || false
        }
        if (payload.reportType === 'pdf') payload.pageSize = interfaceStore.preferences.downloadReport?.pageSize ?? 'a4'
        const fileExtension = FileExtensions[payload.reportType as keyof typeof FileExtensions] ?? 'pdf'
        if (timelineIsTrimmed.value) {
            payload.startTime = startTime.value
            payload.endTime = endTime.value
        }
        try {
            isGeneratingLabelReport.value = true
            const { presignedUrl } = await downloadLabelReport({
                url: `${import.meta.env.VITE_TAPP_API_URL}/labelReports/download`,
                apiKey: readAccessToken.value,
                payload,
                returnType: 'json',
                db: 'both'
            })
            const fileName = `LabelReport_${props.id}.${fileExtension}`

            const response = await fetch(presignedUrl)
            const fileBlob = await response.blob()

            /*
             * Download the pdf
             * For iOS this is a workaround for the fact that the pdf cannot be downloaded from the blob url, but is opened.
             * Other browsers use the regular virtual link approach
             */
            if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
                // Create a data URL instead of a Blob URL
                const reader = new FileReader()
                reader.onloadend = function () {
                    // Force download by using a hidden iframe
                    const iframe = document.createElement('iframe')
                    iframe.style.display = 'none'
                    document.body.appendChild(iframe)

                    // Write the PDF to the iframe with content-disposition header
                    iframe.contentWindow?.document.open()
                    iframe.contentWindow?.document.write(`<html><body><a id="download" href="${reader.result}" download="${fileName}"></a></body></html>`)
                    iframe.contentWindow?.document.close()

                    // Trigger the download
                    const downloadLink = iframe.contentWindow?.document.getElementById('download')
                    downloadLink?.click()

                    // Clean up
                    setTimeout(() => {
                        document.body.removeChild(iframe)
                    }, 100)
                }
                reader.readAsDataURL(fileBlob)
            } else {
                // For other browsers, use the standard approach
                const virtualLink = document.createElement('a')
                const virtualUrl = URL.createObjectURL(fileBlob)
                virtualLink.href = virtualUrl
                virtualLink.download = fileName
                document.body.appendChild(virtualLink)
                virtualLink.click()
                document.body.removeChild(virtualLink)
                URL.revokeObjectURL(virtualUrl)
            }
            addSnackbar({
                title: 'Download report',
                text: `Successfully downloaded`,
                subText: fileName,
                type: SnackbarType.SUCCESS,
                duration: 2000,
                closable: true
            })
            isGeneratingLabelReport.value = false

            emit('close')
        } catch (error) {
            isGeneratingLabelReport.value = false
            console.error(error)
            addSnackbar({
                title: 'Download report',
                text: `Something went wrong`,
                type: SnackbarType.ERROR,
                duration: 3000,
                closable: true
            })
        }
    }
</script>
